import type { Application } from "pixi.js";
import { Time } from "./Time";
import { EventBus, type EngineEvents } from "./EventBus";
import { createDomainBuses, type DomainBuses } from "./buses";
import { RenderLayers } from "@engine/renderer/RenderLayers";
import { Camera2D } from "@engine/renderer/Camera2D";
import { InputManager } from "@engine/input/InputManager";
import { ActionMap, defaultActions } from "@engine/input/ActionMap";
import { AssetLoader } from "@engine/assets/AssetLoader";
import { TextureAtlasManager } from "@engine/assets/TextureAtlasManager";
import { DebugRegistry } from "@engine/debug/DebugRegistry";
import { PerfMonitor } from "@engine/debug/PerfMonitor";
import { DebugOverlay } from "@engine/debug/DebugOverlay";
import { Inspector } from "@engine/debug/Inspector";
import type {
  UpdateSystem,
  RenderSystem,
  FixedUpdateSystem,
  Disposable,
} from "./Lifecycle";

export class Game {
  readonly time = new Time();
  readonly events = new EventBus<EngineEvents>();
  readonly buses: DomainBuses = createDomainBuses();
  readonly layers = new RenderLayers();
  readonly camera: Camera2D;
  readonly input = new InputManager();
  readonly actions: ActionMap = defaultActions();
  readonly assets = new AssetLoader();
  readonly atlases = new TextureAtlasManager();
  readonly debug = new DebugRegistry();
  readonly perf: PerfMonitor;
  readonly overlay: DebugOverlay;
  readonly inspector: Inspector;

  private updateSystems: UpdateSystem[] = [];
  private fixedUpdateSystems: FixedUpdateSystem[] = [];
  private renderSystems: RenderSystem[] = [];
  private disposables: Disposable[] = [];
  private detachInput: (() => void) | undefined;
  private detachResize: (() => void) | undefined;
  private tickerHandle: (() => void) | undefined;
  private running = false;

  constructor(public readonly app: Application) {
    // Pixi v8 with autoDensity uses CSS pixels for stage coords; canvas.width
    // is the DPR-scaled backing store. We MUST use renderer.screen for the
    // logical (stage) viewport size — otherwise camera centers on the wrong
    // point on hi-DPI displays.
    const screen = app.renderer.screen;
    this.camera = new Camera2D(screen.width, screen.height);
    this.layers.attachAll(app.stage);
    this.perf = new PerfMonitor(this);
    this.overlay = new DebugOverlay(this);
    this.inspector = new Inspector(this);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.detachInput = this.input.attach(window);
    this.overlay.attach();
    this.inspector.attach();

    const onResize = () => {
      const screen = this.app.renderer.screen;
      this.camera.setViewport(screen.width, screen.height);
      this.events.emit("viewport:resize", { width: screen.width, height: screen.height });
    };
    window.addEventListener("resize", onResize);
    this.detachResize = () => window.removeEventListener("resize", onResize);

    const handler = () => this.frame();
    this.app.ticker.add(handler);
    this.tickerHandle = handler;
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.tickerHandle) this.app.ticker.remove(this.tickerHandle);
    this.detachInput?.();
    this.detachResize?.();
    this.overlay.detach();
    this.inspector.detachListeners();
    this.tickerHandle = undefined;
    this.detachInput = undefined;
    this.detachResize = undefined;
  }

  addUpdateSystem(sys: UpdateSystem): UpdateSystem {
    this.updateSystems.push(sys);
    return sys;
  }
  addFixedUpdateSystem(sys: FixedUpdateSystem): FixedUpdateSystem {
    this.fixedUpdateSystems.push(sys);
    return sys;
  }
  addRenderSystem(sys: RenderSystem): RenderSystem {
    this.renderSystems.push(sys);
    return sys;
  }
  removeUpdateSystem(sys: UpdateSystem): void {
    const i = this.updateSystems.indexOf(sys);
    if (i >= 0) this.updateSystems.splice(i, 1);
  }
  removeFixedUpdateSystem(sys: FixedUpdateSystem): void {
    const i = this.fixedUpdateSystems.indexOf(sys);
    if (i >= 0) this.fixedUpdateSystems.splice(i, 1);
  }
  removeRenderSystem(sys: RenderSystem): void {
    const i = this.renderSystems.indexOf(sys);
    if (i >= 0) this.renderSystems.splice(i, 1);
  }
  registerDisposable(d: Disposable): void {
    this.disposables.push(d);
  }

  private frame(): void {
    this.time.tick(performance.now());
    const dt = this.time.delta;

    for (const sys of this.updateSystems) sys.update(dt);

    if (this.fixedUpdateSystems.length > 0) {
      this.time.consumeFixedSteps((step) => {
        for (const sys of this.fixedUpdateSystems) sys.fixedUpdate(step);
      });
    }

    const screen = this.app.renderer.screen;
    this.camera.setViewport(screen.width, screen.height);
    this.camera.update(dt);
    this.camera.apply(this.layers.worldRoot);

    this.perf.update(dt);
    this.overlay.render();

    for (const sys of this.renderSystems) sys.render(1);

    this.input.endFrame();
  }

  destroy(): void {
    this.stop();
    this.overlay.destroy();
    this.inspector.destroy();
    this.debug.clear();
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
    this.updateSystems.length = 0;
    this.fixedUpdateSystems.length = 0;
    this.renderSystems.length = 0;
    this.events.clear();
  }
}
